import { Input } from 'antd'
import { SENSOR_COLORS_HEX, useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { isFiniteNumber } from 'features/timeSeries/utils/axisRangeHelpers'
import {
  isPartialNumericInput,
  parseCommittedNumber,
} from 'features/timeSeries/utils/numericInput'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

export const YAxisAssignmentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`
const YAxisItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.5rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.6875rem;
  margin-bottom: 0.375rem;
`
const YAxisItemHeader = styled.div<{ $isClickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  user-select: none;
  padding: 0.25rem;
  margin: -0.25rem;
  border-radius: 0.25rem;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme, $isClickable }) =>
      $isClickable ? theme.forecast.background3 : 'transparent'};
  }
`
const YAxisColorIndicator = styled.div<{ $color: string }>`
  width: 0.1875rem;
  height: 0.875rem;
  background: ${({ $color }) => $color};
  border-radius: 0.125rem;
`
const YAxisUnitName = styled.span`
  color: ${({ theme }) => theme.forecast.activeText};
  flex: 1;

  & span {
    font-size: 0.75rem;
    opacity: 0.7;
  }
`
export const YAxisRangeControls = styled.div`
  display: flex;
  gap: 0.375rem;
  align-items: center;
  margin-left: 1.5rem;
  flex-wrap: wrap;
`
export const YAxisRangeInput = styled(Input)`
  width: 4.5rem;
  height: 1.5rem;
  font-size: 0.625rem;
  padding: 0.125rem 0.25rem;

  input {
    font-size: 0.625rem;
  }
`
export const YAxisRangeLabel = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.625rem;
  min-width: 1.5rem;
`
const YAxisSideToggle = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  margin-left: auto;
`
const YAxisSideButton = styled.button<{ $isActive?: boolean; $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.125rem;
  border: 1px solid
    ${({ theme, $isActive, $color }) =>
      $isActive ? $color : theme.forecast.border2};
  background: ${({ $isActive, $color }) =>
    $isActive ? `${$color}20` : 'transparent'};
  color: ${({ $isActive, $color }) => ($isActive ? $color : `${$color}80`)};
  cursor: pointer;
  border-radius: 0.125rem;
  transition: all 0.2s ease;
  font-size: 0.625rem;
  font-weight: 600;
  height: 1rem;
  min-width: 1rem;

  &:hover {
    background: ${({ $color }) => `${$color}30`};
    border-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
  }

  svg {
    width: 0.625rem;
    height: 0.625rem;
  }
`
export const YAxisAutoButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.375rem;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.forecast.primary : theme.forecast.border};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.forecast.primary + '20' : 'transparent'};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.forecast.primary : theme.forecast.defaultText};
  cursor: pointer;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  font-size: 0.625rem;
  font-weight: 500;
  height: 1.5rem;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.primary};
    color: ${({ theme }) => theme.forecast.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-left: auto;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

interface CommittedRangeInputProps {
  value?: number
  placeholder: string
  onCommit: (value: number | undefined) => void
}

/**
 * Same commit semantics as the pens-table range fields (EditableRangeInput):
 * numeric-only keystrokes, select-on-focus, commit on blur/Enter, Escape
 * reverts. Kept local because this surface styles an antd Input.
 */
const CommittedRangeInput: React.FC<CommittedRangeInputProps> = ({
  value,
  placeholder,
  onCommit,
}) => {
  const committedText = value !== undefined ? value.toString() : ''
  const [localValue, setLocalValue] = useState(committedText)
  const skipBlurCommitRef = useRef(false)

  // Layout effect (not useEffect) so the revert-then-resync below never paints
  // an intermediate frame — same pattern as EditableRangeInput.
  useLayoutEffect(() => {
    setLocalValue(committedText)
  }, [committedText])

  const commit = (inputValue: string) => {
    const parsed = parseCommittedNumber(inputValue)
    if (parsed.ok) {
      if (parsed.value !== value) {
        onCommit(parsed.value)
      }
      // The handler may REJECT the commit (e.g. a min above the effective max
      // is reverted rather than inverting the range). Resyncing to the
      // committed text keeps the field honest either way: on accept the value
      // prop changes and the layout effect above overwrites this with the new
      // text; on reject the field shows the previous value instead of a
      // number that was never applied.
      setLocalValue(committedText)
    } else {
      setLocalValue(committedText)
    }
  }

  return (
    <YAxisRangeInput
      value={localValue}
      placeholder={placeholder}
      onChange={(e) => {
        if (isPartialNumericInput(e.target.value)) {
          setLocalValue(e.target.value)
        }
      }}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={() => {
        if (skipBlurCommitRef.current) {
          skipBlurCommitRef.current = false
          return
        }
        commit(localValue)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          skipBlurCommitRef.current = true
          commit(e.currentTarget.value)
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          skipBlurCommitRef.current = true
          setLocalValue(committedText)
          e.currentTarget.blur()
        }
      }}
    />
  )
}

interface YAxisItemProps {
  sensor: { id: string; name?: string; unit?: string }
  index: number
}

export const YAxisItem: React.FC<YAxisItemProps> = ({ sensor, index }) => {
  const {
    sensorRanges,
    sensorAxisMapping,
    customAxisRanges,
    handleYMinChange,
    handleYMaxChange,
    handleClearAxisRange,
    expandedAxes,
    setExpandedAxes,
    setSensorAxisAssignment,
    isLoading,
    isInitializing,
  } = useScatterplotContext()

  const sensorId = sensor.id
  const sensorName = sensor?.name || sensorId
  const sensorData = sensorRanges[sensorId]
  const axisInfo = sensorAxisMapping[sensorId]

  const color = SENSOR_COLORS_HEX[index % SENSOR_COLORS_HEX.length]
  const customRange = customAxisRanges[sensorId] || {}
  const isExpanded = expandedAxes[sensorId] || false
  const hasCustomRange =
    customRange.min !== undefined || customRange.max !== undefined

  const rangesSettled = !isLoading && !isInitializing
  const autoPlaceholder = (autoValue: number | undefined) => {
    if (isFiniteNumber(autoValue)) return autoValue.toFixed(2)
    return rangesSettled ? 'No data' : '—'
  }

  return (
    <YAxisItemContainer>
      <YAxisItemHeader
        $isClickable
        onClick={() => {
          setExpandedAxes((prev) => ({
            ...prev,
            [sensorId]: !prev[sensorId],
          }))
        }}>
        <YAxisColorIndicator $color={color} />
        <YAxisUnitName>
          {sensorName}
          {sensorData?.unit && sensorData.unit !== 'No Unit' && (
            <span> ({sensorData.unit})</span>
          )}
        </YAxisUnitName>
        <YAxisSideToggle>
          <ChartTooltip label='Move to left axis'>
            <YAxisSideButton
              $isActive={axisInfo?.side === 'left'}
              $color={color}
              onClick={(e) => {
                e.stopPropagation()
                setSensorAxisAssignment((prev) => ({
                  ...prev,
                  [sensorId]: {
                    side: 'left',
                    index: index,
                  },
                }))
              }}>
              <ChevronLeft />
            </YAxisSideButton>
          </ChartTooltip>
          <ChartTooltip label='Move to right axis'>
            <YAxisSideButton
              $isActive={axisInfo?.side === 'right'}
              $color={color}
              onClick={(e) => {
                e.stopPropagation()
                setSensorAxisAssignment((prev) => ({
                  ...prev,
                  [sensorId]: {
                    side: 'right',
                    index: index,
                  },
                }))
              }}>
              <ChevronRight />
            </YAxisSideButton>
          </ChartTooltip>
        </YAxisSideToggle>
        <ExpandIcon>{isExpanded ? <ChevronUp /> : <ChevronDown />}</ExpandIcon>
      </YAxisItemHeader>
      {isExpanded && (
        <YAxisRangeControls>
          <YAxisRangeLabel>Min:</YAxisRangeLabel>
          <CommittedRangeInput
            value={customRange.min}
            placeholder={autoPlaceholder(sensorData?.min)}
            onCommit={(value) =>
              handleYMinChange(
                sensorId,
                value !== undefined ? String(value) : ''
              )
            }
          />
          <YAxisRangeLabel>Max:</YAxisRangeLabel>
          <CommittedRangeInput
            value={customRange.max}
            placeholder={autoPlaceholder(sensorData?.max)}
            onCommit={(value) =>
              handleYMaxChange(
                sensorId,
                value !== undefined ? String(value) : ''
              )
            }
          />
          <ChartTooltip label='Reset to automatic scaling'>
            <YAxisAutoButton
              $isActive={!hasCustomRange}
              onClick={() => handleClearAxisRange(sensorId)}
              disabled={!hasCustomRange}>
              Auto
            </YAxisAutoButton>
          </ChartTooltip>
        </YAxisRangeControls>
      )}
    </YAxisItemContainer>
  )
}
